import type {
  LegPriceBreak,
  SupplierProductCostSource,
} from '@erp/shared-types';
import {
  divideMoney,
  multiplyMoney,
  roundMoney,
  subtractMoney,
  toMoneyNumber,
} from '@erp/shared-utils';

/**
 * What a supplier cost agreement saves on one buy line, against what the same
 * quantity costs on the supplier product row without it (its reached price
 * break, else its list cost), as the engine froze both on the buy leg
 * (docs/procurement/forward-buying.md, Part 2, decision 17).
 *
 * The saving is negative when the agreement costs more than the row: an
 * agreement outranks the row whatever each costs, so the board has to say so.
 */
export interface AgreementSaving {
  agreementUnitCost: number;
  withoutAgreementUnitCost: number;
  withoutAgreementCostSource: SupplierProductCostSource;
  quantity: number;
  /** Per unit, full precision: positive when the agreement is cheaper. */
  unitSaving: number;
  /** On the whole line, to the cent: positive when the agreement is cheaper. */
  totalSaving: number;
  /** Share of the row's cost saved, e.g. 0.59; null when the row costs 0. */
  fractionSaved: number | null;
}

/**
 * The agreement's saving on a buy leg, or null when no agreement priced it or
 * the leg was priced before the row's price was recorded next to it.
 */
export function agreementSavingOf({
  priceBreak,
  quantity,
}: {
  priceBreak: LegPriceBreak | null | undefined;
  quantity: number | null | undefined;
}): AgreementSaving | null {
  if (priceBreak == null || priceBreak.costSource !== 'agreement') return null;
  const { unitCost, withoutAgreementUnitCost, withoutAgreementCostSource } =
    priceBreak;
  if (
    unitCost == null ||
    withoutAgreementUnitCost == null ||
    withoutAgreementCostSource == null ||
    quantity == null ||
    quantity <= 0
  ) {
    return null;
  }
  const unitSaving = subtractMoney(withoutAgreementUnitCost, unitCost);
  return {
    agreementUnitCost: unitCost,
    withoutAgreementUnitCost,
    withoutAgreementCostSource,
    quantity,
    unitSaving: toMoneyNumber(unitSaving),
    totalSaving: toMoneyNumber(roundMoney(multiplyMoney(unitSaving, quantity))),
    fractionSaved:
      withoutAgreementUnitCost === 0
        ? null
        : toMoneyNumber(divideMoney(unitSaving, withoutAgreementUnitCost)),
  };
}
